import React, { useCallback, useState } from "react";
import { Box, Card, CardContent, CardHeader } from "@material-ui/core";
import { map, mergeRight } from "ramda";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import EditIcon from "@material-ui/icons/Edit";
import Grid from "@material-ui/core/Grid";
import { isBlank, notBlank } from "../../util/ext";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import LabeledReadOnly from "../../components/LabeledReadOnly";
import { makeStyles } from "@material-ui/core/styles";
import ReadOnlyField from "../../components/ReadOnlyField";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import { Markup } from "interweave";
import EditLandingPageDialog from "../edit/EditLandingPageDialog";
import { getSocialLinkPlatform } from "../cspServices";

const useStyles = makeStyles(theme => ({
  header: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  content: {
    paddingTop: 0,
  },
}));

const LandingPageCard = ({ project, loading, onUpdate }) => {
  const classes = useStyles();

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const handleEditClick = useCallback(() => {
    console.log("Handling Edit click...");
    setEditDialogOpen(true);
  }, []);

  const handleEditCancel = useCallback(() => {
    setEditDialogOpen(false);
  }, []);

  const handleEditApply = useCallback(
    async data => {
      //call parent callback to save updated data.
      setEditDialogOpen(false);
      console.log(data);
      const update = mergeRight(project, data);
      console.log(update);
      onUpdate(update);
    },
    [project, onUpdate],
  );

  return (
    <Card raised elevation={4} style={{ height: "100%" }}>
      <CardHeader
        titleTypographyProps={{
          variant: "h6",
          color: "primary",
        }}
        title="Landing Page Properties"
        action={
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <Tooltip title="Edit Landing Page Properties">
              <IconButton onClick={handleEditClick}>
                <EditIcon color="primary" fontSize="large" />
              </IconButton>
            </Tooltip>
          </Box>
        }
        classes={{ root: classes.header }}
      />
      <CardContent classes={{ root: classes.content }}>
        {loading && <PanelLoadingIndicator />}
        {isBlank(project) && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            Error Loading Landing Page Details.
          </Box>
        )}
        {notBlank(project) && (
          <Grid container spacing={3}>
            <Grid item sm={9}>
              <Grid container spacing={3}>
                <Grid item sm={4}>
                  <LabeledReadOnly label="Landing Page Template">
                    <ReadOnlyField>{project.landingPageTemplate}</ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={4}>
                  <LabeledReadOnly label="Thank You Page Template">
                    <ReadOnlyField>
                      {project.thankYouPageTemplate}
                    </ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={4}>
                  <LabeledReadOnly label="BiblioBoard Collection URL">
                    <ReadOnlyField>{project.collectionUri}</ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={12}>
                  <LabeledReadOnly label="About this Project">
                    {notBlank(project.projectDescription) ? (
                      <Markup content={project.projectDescription} />
                    ) : (
                      <Typography>--</Typography>
                    )}
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={12}>
                  <LabeledReadOnly label="Additional Information">
                    {notBlank(project.additionalInformation) ? (
                      <Markup content={project.additionalInformation} />
                    ) : (
                      <Typography>--</Typography>
                    )}
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={12}>
                  <LabeledReadOnly label="Age Requirements">
                    <ReadOnlyField>{project.ageRequirements}</ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={12}>
                  <LabeledReadOnly label="Thank You Page Content">
                    {notBlank(project.thankYouMessage) ? (
                      <Markup content={project.thankYouMessage} />
                    ) : (
                      <Typography>--</Typography>
                    )}
                  </LabeledReadOnly>
                </Grid>
              </Grid>
            </Grid>

            <Grid item sm={3}>
              <LabeledReadOnly label="Social Links">
                {notBlank(project.socialLinks) ? (
                  <List dense>
                    {map(
                      sl => (
                        <ListItem key={sl.projectSocialLinkId}>
                          <ListItemText
                            primary={getSocialLinkPlatform(sl.platform).label}
                            secondary={sl.linkUrl}
                          />
                        </ListItem>
                      ),
                      project.socialLinks,
                    )}
                  </List>
                ) : (
                  <Typography>--</Typography>
                )}
              </LabeledReadOnly>
            </Grid>
          </Grid>
        )}
      </CardContent>
      {editDialogOpen && (
        <EditLandingPageDialog
          open={editDialogOpen}
          project={project}
          onCancel={handleEditCancel}
          onApply={handleEditApply}
        />
      )}
    </Card>
  );
};

export default LandingPageCard;
